import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import { useAuth } from "@/lib/auth";
import Spinner from "@/ui/Spinner";

/**
 * Resolve where to send the user after the email-link sign-in completes.
 * `urlForSignIn` is stored as a full href; convert it to a same-origin
 * relative path so Next.js does a client-side transition that preserves the
 * just-populated auth context (a hard navigation would re-show "loading").
 */
const resolveDestination = () => {
    if (typeof window === "undefined") return "/";

    const stored = window.localStorage.getItem("urlForSignIn");
    if (!stored) return "/";

    try {
        const url = new URL(stored, window.location.origin);
        if (url.origin !== window.location.origin) return "/";
        return url.pathname + url.search + url.hash;
    } catch {
        return "/";
    }
};

const FinishSignIn = () => {
    const router = useRouter();
    const auth = useAuth();

    const startedRef = useRef(false);
    const redirectedRef = useRef(false);
    const [linkProcessed, setLinkProcessed] = useState(false);
    const [failed, setFailed] = useState(false);

    // Kick off the email-link sign-in exactly once.
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        (async () => {
            try {
                await auth.checkSignInWithEmailLink(window.location.href);
            } catch (err) {
                console.error("FinishSignIn failed", err);
            } finally {
                setLinkProcessed(true);
            }
        })();
    }, [auth]);

    // Redirect as soon as the shared auth context reports a signed-in user.
    // This covers both a fresh email-link sign-in and an already-signed-in
    // reload (where the link has already been consumed). Only show the error
    // once link processing has finished, auth has settled, and there is still
    // no user.
    useEffect(() => {
        if (redirectedRef.current) return;

        if (auth?.user?.uid) {
            redirectedRef.current = true;
            const destination = resolveDestination();
            window.localStorage.removeItem("urlForSignIn");
            router.replace(destination);
            return;
        }

        if (linkProcessed && !auth?.loading && auth?.user === false) {
            setFailed(true);
        }
    }, [auth, auth?.user, auth?.loading, linkProcessed, router]);

    return (
        <>
            <Head>
                <title>Signing you in…</title>
                <meta name="robots" content="noindex,nofollow" />
            </Head>
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4 text-center">
                {failed ? (
                    <>
                        <h2 className="text-2xl font-headline font-bold text-primary-500 mb-2">
                            This sign-in link is no longer valid
                        </h2>
                        <p className="text-gray-600 mb-6 max-w-md">
                            It may have expired or already been used. Please head back
                            and request a new sign-in link.
                        </p>
                        <button
                            onClick={() => router.replace("/")}
                            className="px-6 py-2.5 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-400 transition-colors"
                        >
                            Go home
                        </button>
                    </>
                ) : (
                    <>
                        <Spinner size="xl" className="w-10 h-10 text-primary-300" />
                        <p className="mt-4 text-gray-600">Signing you in…</p>
                    </>
                )}
            </div>
        </>
    );
};

export default FinishSignIn;
